import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Search, X, LayoutGrid, List, Package,
    Building2, FolderOpen, Layers, Clock, Tag,
    ChevronDown, ArrowUpDown, Eye, ChevronRight,
    Loader2, Sparkles, ZoomIn, ExternalLink, FileText, Bookmark, Save, Trash2, Star,
    GripVertical,
} from 'lucide-react';
import {
    useSearchContentVersionsLazyQuery,
    useGetClientsQuery,
    useGetCategoryListQuery,
    useGetSubCategoryListQuery,
    useGetApplicationNameListQuery,
    useGetSavedSearchesQuery,
    useGetTagListQuery,
    useGetContentIdsByTagsLazyQuery,
    useGetMeQuery,
    useGetMyDynamicDataQuery,
    useSetMyDynamicDataMutation,
    useDeleteMyDynamicDataMutation,
    ContentVersionFilterInput,
} from '../../generated/graphql';
import SpinnerNew from '../../components/main/SpinnerNew/SpinnerNew';
import TagBadge from '../../components/main/TagBadge/TagBadge';

// ─── Constants ───────────────────────────────────────────────────
const PAGE_SIZE = 50;

// ─── Utility: Format epoch timestamp ─────────────────────────────
function formatDate(epoch?: string | null): string {
    if (!epoch) return '';
    const date = new Date(Number(epoch) * 1000);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Utility: Open PDFs in a shared viewer tab ──────────────────
let pdfViewerWindow: Window | null = null;

async function openPdfInNewTab(url: string, name: string) {
    const fileName = name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`;
    let blobUrl: string;
    try {
        const resp = await fetch(url);
        const blob = await resp.blob();
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        blobUrl = URL.createObjectURL(pdfBlob);
    } catch {
        window.open(url, '_blank');
        return;
    }

    if (!pdfViewerWindow || pdfViewerWindow.closed) {
        pdfViewerWindow = window.open('', '_blank');
        if (!pdfViewerWindow) { URL.revokeObjectURL(blobUrl); return; }
        pdfViewerWindow.document.write(`<!DOCTYPE html>
<html><head><title>${fileName}</title>
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body { background: #1a1a1a; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
.pdf-header { position: sticky; top: 0; z-index: 10; background: #2a2a2a; border-bottom: 1px solid #444;
              padding: 12px 20px; display: flex; align-items: center; gap: 12px; }
.pdf-header h1 { color: #e0e0e0; font-size: 14px; font-weight: 600; flex: 1; }
.pdf-count { background: #4d7c0f; color: white; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 10px; }
.pdf-dl-all, .pdf-layout-btn { display: inline-flex; align-items: center; gap: 5px; padding: 6px 14px; border-radius: 6px;
              font-size: 12px; font-weight: 600; color: #fff; background: #4d7c0f; border: none; cursor: pointer; transition: background 0.15s; }
.pdf-dl-all:hover, .pdf-layout-btn:hover { background: #3d6b0c; }
.pdf-dl-all:disabled { opacity: 0.5; cursor: default; }
.pdf-layout-btn.active { background: #3d6b0c; box-shadow: inset 0 0 0 1.5px #6b9e2a; }
#pdf-container.layout-stack .pdf-section { border-bottom: 2px solid #333; }
#pdf-container.layout-stack .pdf-embed { width: 100%; height: 92vh; border: none; display: block; }
#pdf-container.layout-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(500px, 1fr)); gap: 4px; padding: 4px; }
#pdf-container.layout-grid .pdf-section { border: 1px solid #333; border-radius: 6px; overflow: hidden; display: flex; flex-direction: column; }
#pdf-container.layout-grid .pdf-embed { width: 100%; flex: 1; min-height: 70vh; border: none; display: block; }
.pdf-label { background: #2a2a2a; padding: 8px 12px; display: flex; align-items: center; gap: 8px; }
.pdf-label .pdf-name { color: #ccc; font-size: 12px; font-weight: 500; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pdf-label .pdf-num { color: #888; font-size: 11px; }
.pdf-dl { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 6px;
          font-size: 11px; font-weight: 600; text-decoration: none; color: #4d7c0f; background: rgba(77,124,15,0.12);
          border: 1px solid rgba(77,124,15,0.3); transition: background 0.15s; cursor: pointer; }
.pdf-dl:hover { background: rgba(77,124,15,0.25); }
</style></head><body>
<div class="pdf-header">
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4d7c0f" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
  <h1>Content PDF Viewer</h1>
  <span class="pdf-count" id="pdf-count">0</span>
  <button class="pdf-layout-btn" id="btn-stack" title="Stack view" onclick="setLayout('stack')">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="7" rx="1"/><rect x="3" y="14" width="18" height="7" rx="1"/></svg>
  </button>
  <button class="pdf-layout-btn active" id="btn-grid" title="Grid view" onclick="setLayout('grid')">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
  </button>
  <button class="pdf-dl-all" id="dl-all-btn" disabled onclick="downloadAll()">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
    Download All
  </button>
</div>
<div id="pdf-container" class="layout-grid"></div>
<script>
window.__pdfFiles = [];
function downloadAll() { var f = window.__pdfFiles || []; f.forEach(function(x,i){ setTimeout(function(){ var a=document.createElement('a'); a.href=x.blobUrl; a.download=x.fileName; document.body.appendChild(a); a.click(); document.body.removeChild(a); },i*300); }); }
function setLayout(m) { var c=document.getElementById('pdf-container'); var s=document.getElementById('btn-stack'); var g=document.getElementById('btn-grid'); if(!c)return; c.className='layout-'+m; s.className='pdf-layout-btn'+(m==='stack'?' active':''); g.className='pdf-layout-btn'+(m==='grid'?' active':''); }
</script></body></html>`);
        pdfViewerWindow.document.close();
    }

    const doc = pdfViewerWindow.document;
    const container = doc.getElementById('pdf-container');
    const countEl = doc.getElementById('pdf-count');
    if (!container) { URL.revokeObjectURL(blobUrl); return; }
    const num = container.children.length + 1;
    const section = doc.createElement('div');
    section.className = 'pdf-section';
    section.id = `pdf-${num}`;
    section.innerHTML = `<div class="pdf-label"><span class="pdf-name">${name}</span><a class="pdf-dl" href="${blobUrl}" download="${fileName}"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>Download</a><span class="pdf-num">#${num}</span></div><iframe src="${blobUrl}" class="pdf-embed"></iframe>`;
    container.appendChild(section);
    const win = pdfViewerWindow as any;
    if (win.__pdfFiles) win.__pdfFiles.push({ blobUrl, fileName });
    const dlAllBtn = doc.getElementById('dl-all-btn') as HTMLButtonElement | null;
    if (dlAllBtn) dlAllBtn.disabled = false;
    if (countEl) countEl.textContent = String(num);
    doc.title = num === 1 ? fileName : `${num} PDFs — Content Viewer`;
    section.scrollIntoView({ behavior: 'smooth' });
    if (num === 1) pdfViewerWindow.focus();
}

// ─── Types ───────────────────────────────────────────────────────
type SortOption = 'newest' | 'oldest' | 'name-asc' | 'name-desc' | 'popular';

const sortLabels: Record<SortOption, string> = {
    'newest': 'Newest First',
    'oldest': 'Oldest First',
    'name-asc': 'Name A→Z',
    'name-desc': 'Name Z→A',
    'popular': 'Most Used',
};

// ─── Thumbnail helpers ───────────────────────────────────────────
function getPrimaryThumbnail(cv: any): string | null {
    return cv.thumbnail?.downloadUrl || cv.content?.thumbnail?.downloadUrl || null;
}

function getContentDisplayName(cv: any): string {
    return cv.content?.name || cv.name || '';
}

function getTypeThumbnails(cv: any): { url: string; name: string }[] {
    if (!cv.types) return [];
    return cv.types
        .filter((t: any) => t?.thumbnail?.downloadUrl)
        .slice(0, 5)
        .map((t: any) => ({ url: t.thumbnail.downloadUrl, name: t.name || '' }));
}

const ThumbnailDisplay = ({ cv, onZoom }: { cv: any; onZoom: (url: string) => void }) => {
    const primary = getPrimaryThumbnail(cv);
    const typeThumbs = getTypeThumbnails(cv);

    if (primary) {
        return (
            <>
                <img src={primary} alt={getContentDisplayName(cv)} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <div
                    className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: 'rgba(0,0,0,0.25)', cursor: 'zoom-in' }}
                    onClick={e => { e.stopPropagation(); onZoom(primary); }}
                >
                    <ZoomIn size={22} style={{ color: 'white' }} />
                </div>
                {typeThumbs.length > 0 && (
                    <div className="absolute bottom-0 left-0 right-0 flex gap-1 p-1.5 z-10"
                        style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.5))' }}>
                        {typeThumbs.map((t, i) => (
                            <div key={i} className="relative cursor-pointer" title={t.name}
                                onClick={e => { e.stopPropagation(); onZoom(t.url); }}>
                                <img src={t.url} alt={t.name} className="rounded hover:ring-2 hover:ring-white transition-all"
                                    style={{ width: 32, height: 32, objectFit: 'cover', border: '1.5px solid rgba(255,255,255,0.6)' }} />
                            </div>
                        ))}
                    </div>
                )}
            </>
        );
    }
    if (typeThumbs.length > 0) {
        return (
            <div className="flex flex-wrap items-center justify-center gap-2 p-3 w-full h-full">
                {typeThumbs.map((t, i) => (
                    <div key={i} className="flex flex-col items-center gap-1 cursor-pointer" title={t.name}
                        onClick={e => { e.stopPropagation(); onZoom(t.url); }}>
                        <img src={t.url} alt={t.name} className="rounded-lg shadow-sm hover:ring-2 hover:ring-white transition-all"
                            style={{ width: 56, height: 56, objectFit: 'cover', border: '1px solid var(--border-default)' }} />
                        <span className="text-xs truncate" style={{ color: 'var(--text-tertiary)', maxWidth: 60, fontSize: '9px' }}>{t.name}</span>
                    </div>
                ))}
            </div>
        );
    }
    return <Package size={40} style={{ color: 'var(--text-tertiary)', opacity: 0.4 }} />;
};

// ─── Reusable Multi-Select Filter Dropdown ───────────────────────
type FilterSelectProps = {
    label: string;
    values: string[];
    options: string[];
    placeholder: string;
    onChange: (values: string[]) => void;
    icon?: React.ReactNode;
};

const FilterSelect = ({ label, values, options, placeholder, onChange, icon }: FilterSelectProps) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));

    const toggle = (opt: string) => {
        if (values.includes(opt)) {
            onChange(values.filter(v => v !== opt));
        } else {
            onChange([...values, opt]);
        }
    };

    const displayText = values.length === 0
        ? placeholder
        : values.length === 1
            ? values[0]
            : `${values[0]} +${values.length - 1} more`;

    return (
        <div className="relative" ref={ref}>
            <label className="section-title block mb-1.5" style={{ fontSize: '11px' }}>{label}</label>
            <button
                className="input w-full text-left flex items-center gap-2"
                style={{ cursor: 'pointer' }}
                onClick={() => { setOpen(!open); setSearch(''); }}
            >
                {icon}
                <span className="flex-1 truncate" style={{ color: values.length > 0 ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                    {displayText}
                </span>
                {values.length > 0 && (
                    <span className="flex items-center justify-center rounded-full text-xs font-semibold"
                        style={{ width: 20, height: 20, backgroundColor: 'var(--accent)', color: 'white', flexShrink: 0 }}>
                        {values.length}
                    </span>
                )}
                <ChevronDown size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
            </button>
            {open && (
                <div className="absolute top-full left-0 right-0 mt-1 card p-2 z-50 max-h-64 overflow-auto"
                    style={{ boxShadow: 'var(--shadow-lg)' }}>
                    {options.length > 8 && (
                        <input type="text" className="input w-full mb-2"
                            style={{ fontSize: '13px', backgroundColor: 'var(--surface-0)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '8px 12px', outline: 'none' }}
                            placeholder={`Search ${label.toLowerCase()}...`}
                            value={search} onChange={e => setSearch(e.target.value)} autoFocus />
                    )}
                    {values.length > 0 && (
                        <button className="w-full text-left px-3 py-1.5 rounded-md text-xs font-medium transition-colors mb-1"
                            style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => onChange([])}>
                            Clear all ({values.length})
                        </button>
                    )}
                    {filtered.map(opt => {
                        const selected = values.includes(opt);
                        return (
                            <button key={opt} className="w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2"
                                style={{ backgroundColor: selected ? 'var(--accent-soft)' : 'transparent', color: selected ? 'var(--accent)' : 'var(--text-primary)', border: 'none', cursor: 'pointer' }}
                                onMouseEnter={e => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                                onMouseLeave={e => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                onClick={() => toggle(opt)}>
                                <span className="flex items-center justify-center rounded"
                                    style={{ width: 16, height: 16, flexShrink: 0, border: selected ? 'none' : '1.5px solid var(--border-default)', backgroundColor: selected ? 'var(--accent)' : 'transparent', color: 'white', fontSize: '11px' }}>
                                    {selected && '✓'}
                                </span>
                                {opt}
                            </button>
                        );
                    })}
                    {filtered.length === 0 && (
                        <p className="text-xs text-center py-2" style={{ color: 'var(--text-tertiary)' }}>No matches</p>
                    )}
                </div>
            )}
        </div>
    );
};

// ─── Pane Props ──────────────────────────────────────────────────
export type ContentSearchPaneProps = {
    paneId: string;
    /** When true, result cards are draggable and the pane accepts drops */
    splitMode?: boolean;
    /** Called when a card from a DIFFERENT pane is dropped here */
    onDrop?: (sourceContentId: string, sourceContentName: string, targetPaneId: string) => void;
    /** Called when the user clicks a content card to open the detail dialog */
    onOpenDetail?: (contentId: string, clientId: string) => void;
    /** Lightbox handler */
    onZoom?: (url: string) => void;
    /** Compact: hide saved searches and some chrome in split mode */
    compact?: boolean;
    /** Whether this pane is currently being dragged over */
    isDragOver?: boolean;
    onDragOverChange?: (over: boolean) => void;
    /** Called when a card is dropped onto a specific card in this pane (card-to-card linking) */
    onCardDrop?: (sourceContentId: string, sourceContentName: string, targetContentId: string, targetContentName: string) => void;
};

// ─── Pane Component ──────────────────────────────────────────────
const ContentSearchPane = (props: ContentSearchPaneProps) => {
    const { paneId, splitMode, onDrop, onOpenDetail, onZoom, compact, isDragOver, onDragOverChange, onCardDrop } = props;
    const searchInputRef = useRef<HTMLInputElement>(null);

    // ── Filter state ─────────────────────────────────────────────
    const [searchText, setSearchText] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
    const [subCategoryFilter, setSubCategoryFilter] = useState<string[]>([]);
    const [appNameFilter, setAppNameFilter] = useState<string[]>([]);
    const [tagFilter, setTagFilter] = useState<string[]>([]);
    const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

    // ── Display state ────────────────────────────────────────────
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [sortBy, setSortBy] = useState<SortOption>('newest');
    const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

    // ── Search execution state ───────────────────────────────────
    const [hasSearched, setHasSearched] = useState(false);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    // ── Fetch reference data ─────────────────────────────────────
    const clientsResult = useGetClientsQuery({ variables: { contains: '' } });
    const allFetchedClients = (clientsResult.data?.ClientList?.nodes || []).filter(Boolean) as any[];
    // Dropdown: non-private clients + private clients where user is admin
    const clients = allFetchedClients.filter((c: any) => !c.isPrivate || c.isAdmin);
    const selectedClient = clients.find((c: any) => c?.id === selectedClientId);
    // accessibleClientIds exactly matches the dropdown — "All Clients" search
    // must not return content from clients that aren't selectable in the UI
    const accessibleClientIds = useMemo(
        () => clients.map((c: any) => c.id as string),
        [clients]
    );

    const categoryListResult = useGetCategoryListQuery();
    const subCategoryListResult = useGetSubCategoryListQuery();
    const appNameListResult = useGetApplicationNameListQuery();

    const categories = useMemo(() => (categoryListResult.data?.CategoryList || []).filter(Boolean).sort() as string[], [categoryListResult.data]);
    const subCategories = useMemo(() => (subCategoryListResult.data?.SubCategoryList || []).filter(Boolean).sort() as string[], [subCategoryListResult.data]);
    const appNames = useMemo(() => (appNameListResult.data?.ApplicationNameList || []).filter(Boolean).sort() as string[], [appNameListResult.data]);

    const tagListResult = useGetTagListQuery({ variables: { contains: '' }, notifyOnNetworkStatusChange: true });
    const { data: tagData, loading: tagsLoading, error: tagsError, fetchMore: fetchMoreTags } = tagListResult;
    const requestedTagCursor = useRef<string | null>(null);

    useEffect(() => {
        const pageInfo = tagData?.TagList?.pageInfo;
        if (tagsLoading || tagsError || !pageInfo?.hasNextPage || !pageInfo.endCursor
            || requestedTagCursor.current === pageInfo.endCursor) return;

        requestedTagCursor.current = pageInfo.endCursor;
        fetchMoreTags({
            variables: { contains: '', after: pageInfo.endCursor },
            updateQuery: (previous, { fetchMoreResult }) => ({
                ...previous,
                TagList: fetchMoreResult?.TagList && previous.TagList ? {
                    ...fetchMoreResult.TagList,
                    nodes: [...(previous.TagList.nodes || []), ...(fetchMoreResult.TagList.nodes || [])],
                } : previous.TagList,
            }),
        }).catch(error => console.error('Failed to load more tags:', error));
    }, [tagData, tagsLoading, tagsError, fetchMoreTags]);

    const allTags = useMemo(() => {
        const nodes = (tagListResult.data?.TagList?.nodes || []).filter(Boolean) as any[];
        return nodes.map((t: any) => ({
            id: t.id as string,
            name: t.name as string,
            type: t.type as string,
        })).sort((a, b) => a.name.localeCompare(b.name));
    }, [tagListResult.data]);
    const tagOptions = useMemo(() => allTags.map(t => t.name), [allTags]);

    // ── Saved searches ───────────────────────────────────────────
    const savedSearchesResult = useGetSavedSearchesQuery();
    const [savedSearchOpen, setSavedSearchOpen] = useState(false);
    const savedSearchRef = useRef<HTMLDivElement>(null);

    const meResult = useGetMeQuery();
    const myUserId = meResult.data?.Me?.id || null;
    const myDynamicDataResult = useGetMyDynamicDataQuery();
    const [setMyDynamicData] = useSetMyDynamicDataMutation();
    const [deleteMyDynamicData] = useDeleteMyDynamicDataMutation();

    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [saveSearchName, setSaveSearchName] = useState('');
    const [saving, setSaving] = useState(false);
    const saveInputRef = useRef<HTMLInputElement>(null);

    interface SavedSearchSettings {
        clients?: string[];
        applicationNames?: string[];
        categories?: string[];
        subCategories?: string[];
        tags?: string[];
        name?: string;
    }

    const librarySavedSearches = useMemo(() => {
        const nodes = (savedSearchesResult.data?.ContentList?.nodes || []).filter(Boolean) as any[];
        return nodes.map(n => {
            let settings: SavedSearchSettings = {};
            try { settings = JSON.parse(n.settings || '{}'); } catch { }
            return { id: n.id as string, label: n.name as string, settings, source: 'library' as const };
        }).filter(s => s.label);
    }, [savedSearchesResult.data]);

    const [userSavedSearches, setUserSavedSearches] = useState<{ id: string; label: string; settings: SavedSearchSettings; source: 'user' }[]>([]);

    useEffect(() => {
        let cancelled = false;
        const entries = (myDynamicDataResult.data?.Me?.dynamicData || []).filter(Boolean) as any[];
        const savedSearchEntries = entries.filter((d: any) => d.type === 'SavedSearch');
        if (savedSearchEntries.length === 0) { setUserSavedSearches([]); return; }
        (async () => {
            const results: { id: string; label: string; settings: SavedSearchSettings; source: 'user' }[] = [];
            for (const d of savedSearchEntries) {
                let settings: SavedSearchSettings = {};
                if (d.data) {
                    try { const resp = await fetch(d.data); if (resp.ok) { settings = JSON.parse(await resp.text()); } } catch { }
                }
                if (d.name) results.push({ id: d.id, label: d.name, settings, source: 'user' });
            }
            if (!cancelled) setUserSavedSearches(results);
        })();
        return () => { cancelled = true; };
    }, [myDynamicDataResult.data]);

    const savedSearches = useMemo(() => [...userSavedSearches, ...librarySavedSearches], [userSavedSearches, librarySavedSearches]);

    // ── Favourites ───────────────────────────────────────────────
    interface FavouriteEntry { id: string; name: string; }
    const [favouriteIds, setFavouriteIds] = useState<Set<string>>(new Set());
    const [favouritesList, setFavouritesList] = useState<FavouriteEntry[]>([]);
    const [togglingFavourite, setTogglingFavourite] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        const entries = (myDynamicDataResult.data?.Me?.dynamicData || []).filter(Boolean) as any[];
        const favEntry = entries.find((d: any) => d.type === 'Favourites');
        if (!favEntry?.data) { setFavouriteIds(new Set()); setFavouritesList([]); return; }
        (async () => {
            try { const resp = await fetch(favEntry.data); if (resp.ok) { const list: FavouriteEntry[] = JSON.parse(await resp.text()); if (!cancelled) { setFavouritesList(list); setFavouriteIds(new Set(list.map(f => f.id))); } } } catch { }
        })();
        return () => { cancelled = true; };
    }, [myDynamicDataResult.data]);

    const uploadBlobNoCache = useCallback(async (sasUrl: string, data: string) => {
        const response = await fetch(sasUrl, { method: 'PUT', headers: { 'x-ms-blob-type': 'BlockBlob', 'x-ms-blob-cache-control': 'no-cache, no-store, must-revalidate', 'Content-Type': 'application/json' }, body: data });
        if (!response.ok) throw new Error(`Failed to upload data. Status: ${response.status}`);
    }, []);

    const handleSaveSearch = useCallback(async () => {
        if (!saveSearchName.trim() || !myUserId) return;
        setSaving(true);
        try {
            const criteria: SavedSearchSettings = { clients: selectedClient ? [(selectedClient as any).name] : [], applicationNames: appNameFilter, categories: categoryFilter, subCategories: subCategoryFilter, tags: tagFilter, name: searchText };
            await setMyDynamicData({ variables: { userId: myUserId, data: { name: saveSearchName.trim(), type: 'SavedSearch' } } });
            const refetchResult = await myDynamicDataResult.refetch();
            const entries = (refetchResult.data?.Me?.dynamicData || []).filter(Boolean) as any[];
            const newEntry = entries.find((d: any) => d.type === 'SavedSearch' && d.name === saveSearchName.trim());
            if (newEntry?.setData) await uploadBlobNoCache(newEntry.setData, JSON.stringify(criteria));
            await myDynamicDataResult.refetch();
            setSaveDialogOpen(false);
            setSaveSearchName('');
        } catch (err) { console.error('Failed to save search:', err); } finally { setSaving(false); }
    }, [saveSearchName, myUserId, selectedClient, appNameFilter, categoryFilter, subCategoryFilter, tagFilter, searchText, setMyDynamicData, myDynamicDataResult, uploadBlobNoCache]);

    const handleDeleteUserSearch = useCallback(async (id: string) => {
        if (!myUserId || !id) return;
        // Optimistically remove from local state immediately for instant feedback
        setUserSavedSearches(prev => prev.filter(s => s.id !== id));
        try {
            await deleteMyDynamicData({ variables: { userId: myUserId, ids: [id] } });
            await myDynamicDataResult.refetch();
        } catch (err) {
            console.error('Failed to delete saved search:', err);
            // Restore the item on failure by re-fetching
            await myDynamicDataResult.refetch();
        }
    }, [myUserId, deleteMyDynamicData, myDynamicDataResult]);

    const toggleFavourite = useCallback(async (contentId: string, contentName: string) => {
        if (!myUserId || togglingFavourite) return;
        setTogglingFavourite(contentId);
        try {
            const entries = (myDynamicDataResult.data?.Me?.dynamicData || []).filter(Boolean) as any[];
            let favEntry = entries.find((d: any) => d.type === 'Favourites');
            if (!favEntry) { await setMyDynamicData({ variables: { userId: myUserId, data: { name: 'Favourites', type: 'Favourites' } } }); const r = await myDynamicDataResult.refetch(); favEntry = (r.data?.Me?.dynamicData || []).filter(Boolean).find((d: any) => d.type === 'Favourites'); }
            if (!favEntry?.setData) return;
            const updated = favouriteIds.has(contentId) ? favouritesList.filter(f => f.id !== contentId) : [...favouritesList, { id: contentId, name: contentName }];
            await uploadBlobNoCache(favEntry.setData, JSON.stringify(updated));
            setFavouritesList(updated);
            setFavouriteIds(new Set(updated.map(f => f.id)));
            await myDynamicDataResult.refetch();
        } catch (err) { console.error('Failed to toggle favourite:', err); } finally { setTogglingFavourite(null); }
    }, [myUserId, togglingFavourite, myDynamicDataResult, favouriteIds, favouritesList, setMyDynamicData, uploadBlobNoCache]);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => { if (savedSearchRef.current && !savedSearchRef.current.contains(e.target as Node)) setSavedSearchOpen(false); };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const applySavedSearch = useCallback((settings: SavedSearchSettings) => {
        setSearchText(settings.name || '');
        if (settings.clients?.length) { const match = clients.find((c: any) => settings.clients!.some(sc => sc.toLowerCase() === c.name?.toLowerCase())); setSelectedClientId(match?.id || null); } else { setSelectedClientId(null); }
        if (settings.categories?.length) { setCategoryFilter(categories.filter(c => settings.categories!.some(sc => sc.toLowerCase() === c.toLowerCase()))); } else { setCategoryFilter([]); }
        if (settings.subCategories?.length) { setSubCategoryFilter(subCategories.filter(s => settings.subCategories!.some(ss => ss.toLowerCase() === s.toLowerCase()))); } else { setSubCategoryFilter([]); }
        if (settings.applicationNames?.length) { setAppNameFilter(appNames.filter(a => settings.applicationNames!.some(sa => sa.toLowerCase() === a.toLowerCase()))); } else { setAppNameFilter([]); }
        if (settings.tags?.length) { setTagFilter(tagOptions.filter(t => settings.tags!.some(st => st.toLowerCase() === t.toLowerCase()))); } else { setTagFilter([]); }
        setSavedSearchOpen(false);
        setVisibleCount(PAGE_SIZE);
    }, [clients, categories, subCategories, appNames, tagOptions]);

    // ── Lazy queries ─────────────────────────────────────────────
    const [executeSearch, { data, loading: searchLoading }] = useSearchContentVersionsLazyQuery({ fetchPolicy: 'network-only' });
    const [fetchContentIdsByTags, { loading: tagLookupLoading }] = useGetContentIdsByTagsLazyQuery({ fetchPolicy: 'network-only' });
    const loading = searchLoading || tagLookupLoading;

    const buildWhere = useCallback((): ContentVersionFilterInput | undefined => {
        const conditions: ContentVersionFilterInput[] = [];
        if (searchText.trim()) conditions.push({ or: [{ name: { contains: searchText.trim() } }, { description: { contains: searchText.trim() } }] });
        if (selectedClientId) {
            conditions.push({ clientId: { eq: selectedClientId } });
        } else if (accessibleClientIds.length > 0) {
            // Restrict to only clients the user has access to (public + project-accessible private)
            conditions.push({ clientId: { in: accessibleClientIds } });
        }
        if (categoryFilter.length > 0) conditions.push({ category: { in: categoryFilter } });
        if (subCategoryFilter.length > 0) conditions.push({ subCategory: { in: subCategoryFilter } });
        if (appNameFilter.length > 0) conditions.push({ applicationName: { in: appNameFilter } });
        if (conditions.length === 0) return undefined;
        if (conditions.length === 1) return conditions[0];
        return { and: conditions };
    }, [searchText, selectedClientId, categoryFilter, subCategoryFilter, appNameFilter, accessibleClientIds]);

    const [showingFavourites, setShowingFavourites] = useState(false);

    const runSearch = useCallback(async () => {
        setShowingFavourites(false);
        setHasSearched(true);
        setVisibleCount(PAGE_SIZE);
        let where = buildWhere();
        if (tagFilter.length > 0) {
            const selectedTagIds = allTags.filter(t => tagFilter.includes(t.name)).map(t => t.id);
            if (selectedTagIds.length > 0) {
                const tagResult = await fetchContentIdsByTags({ variables: { tagIds: selectedTagIds } });
                const contentIds = (tagResult.data?.ContentList?.nodes || []).filter(Boolean).map((n: any) => n.id as string);
                if (contentIds.length === 0) { executeSearch({ variables: { first: 1, where: { contentId: { eq: '__no_match__' } } } }); return; }
                const tagCondition: ContentVersionFilterInput = { contentId: { in: contentIds } };
                where = where ? { and: [...(where.and || [where]), tagCondition] } : tagCondition;
            }
        }
        executeSearch({ variables: { first: 500, where } });
    }, [executeSearch, fetchContentIdsByTags, buildWhere, tagFilter, allTags]);

    const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') runSearch(); };

    const serverResults = useMemo(() => {
        const allVersions = (data?.ContentVersionList?.nodes || []).filter(Boolean) as any[];
        return allVersions.filter(cv => cv.id && cv.id === cv.content?.currentVersionId);
    }, [data]);

    const filteredResults = useMemo(() => {
        let items = [...serverResults];
        switch (sortBy) {
            case 'newest': items.sort((a, b) => Number(b.createdTime || 0) - Number(a.createdTime || 0)); break;
            case 'oldest': items.sort((a, b) => Number(a.createdTime || 0) - Number(b.createdTime || 0)); break;
            case 'name-asc': items.sort((a, b) => getContentDisplayName(a).localeCompare(getContentDisplayName(b))); break;
            case 'name-desc': items.sort((a, b) => getContentDisplayName(b).localeCompare(getContentDisplayName(a))); break;
            case 'popular': items.sort((a, b) => (b.content?.usageCount || 0) - (a.content?.usageCount || 0)); break;
        }
        return items;
    }, [serverResults, sortBy]);

    const visibleResults = filteredResults.slice(0, visibleCount);
    const hasMore = visibleCount < filteredResults.length;
    const activeFilterCount = (selectedClientId ? 1 : 0) + categoryFilter.length + subCategoryFilter.length + appNameFilter.length + tagFilter.length;
    const hasAnyCriteria = searchText.trim() || activeFilterCount > 0;

    const showFavourites = useCallback(() => {
        if (favouriteIds.size === 0) return;
        setShowingFavourites(true);
        setHasSearched(true);
        setVisibleCount(PAGE_SIZE);
        executeSearch({ variables: { first: 500, where: { contentId: { in: Array.from(favouriteIds) } } } });
    }, [favouriteIds, executeSearch]);

    const clearAllFilters = () => {
        setSearchText(''); setCategoryFilter([]); setSubCategoryFilter([]); setAppNameFilter([]); setTagFilter([]); setSelectedClientId(null); setHasSearched(false);
        searchInputRef.current?.focus();
    };

    useEffect(() => { const timer = setTimeout(() => searchInputRef.current?.focus(), 100); return () => clearTimeout(timer); }, []);
    useEffect(() => { const h = () => setSortDropdownOpen(false); document.addEventListener('click', h); return () => document.removeEventListener('click', h); }, []);

    const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
    const [clientSearchText, setClientSearchText] = useState('');
    const clientRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const h = (e: MouseEvent) => { if (clientRef.current && !clientRef.current.contains(e.target as Node)) setClientDropdownOpen(false); };
        document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
    }, []);
    const filteredClients = clients.filter((c: any) => c?.name?.toLowerCase().includes(clientSearchText.toLowerCase()));

    // ─── Drag-and-drop ───────────────────────────────────────────
    const handleDragStart = (e: React.DragEvent, contentId: string, contentName: string) => {
        e.dataTransfer.setData('application/x-content-id', contentId);
        e.dataTransfer.setData('application/x-content-name', contentName);
        e.dataTransfer.setData('application/x-source-pane', paneId);
        e.dataTransfer.effectAllowed = 'link';
    };

    const handleDragOver = (e: React.DragEvent) => {
        const sourcePane = e.dataTransfer.types.includes('application/x-source-pane');
        if (sourcePane) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'link';
            onDragOverChange?.(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        // Only fire if we actually left the pane (not a child element)
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        onDragOverChange?.(false);
    };

    const handleDropOnPane = (e: React.DragEvent) => {
        e.preventDefault();
        onDragOverChange?.(false);
        setCardDragOver(null);
        const sourceContentId = e.dataTransfer.getData('application/x-content-id');
        const sourceContentName = e.dataTransfer.getData('application/x-content-name');
        const sourcePane = e.dataTransfer.getData('application/x-source-pane');
        if (sourceContentId && sourcePane !== paneId && onDrop) {
            onDrop(sourceContentId, sourceContentName, paneId);
        }
    };

    // ─── Card-level drop (card-to-card linking) ──────────────────
    const [cardDragOver, setCardDragOver] = useState<string | null>(null);

    const handleCardDragOver = (e: React.DragEvent, _targetId: string) => {
        const sourcePane = e.dataTransfer.types.includes('application/x-source-pane');
        if (sourcePane) {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'link';
            setCardDragOver(_targetId);
        }
    };

    const handleCardDragLeave = (e: React.DragEvent) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setCardDragOver(null);
    };

    const handleCardDropOnCard = (e: React.DragEvent, targetContentId: string, targetContentName: string) => {
        e.preventDefault();
        e.stopPropagation();
        setCardDragOver(null);
        onDragOverChange?.(false);
        const sourceContentId = e.dataTransfer.getData('application/x-content-id');
        const sourceContentName = e.dataTransfer.getData('application/x-content-name');
        const sourcePane = e.dataTransfer.getData('application/x-source-pane');
        if (sourceContentId && sourcePane !== paneId && onCardDrop) {
            onCardDrop(sourceContentId, sourceContentName, targetContentId, targetContentName);
        }
    };

    // ═════════════════════════════════════════════════════════════
    // RENDER
    // ═════════════════════════════════════════════════════════════
    return (
        <div
            className="flex flex-col gap-4 flex-1 min-w-0 min-h-0"
            onDragOver={splitMode ? handleDragOver : undefined}
            onDragLeave={splitMode ? handleDragLeave : undefined}
            onDrop={splitMode ? handleDropOnPane : undefined}
            style={{
                position: 'relative',
                borderRadius: splitMode ? '12px' : undefined,
                outline: isDragOver ? '2px dashed var(--accent)' : 'none',
                outlineOffset: '-2px',
                transition: 'outline 0.15s, background-color 0.15s',
                backgroundColor: isDragOver ? 'var(--accent-soft)' : undefined,
            }}
        >
            {/* Drop overlay */}
            {isDragOver && (
                <div className="absolute inset-0 z-50 flex items-center justify-center rounded-xl pointer-events-none animate-fade-in"
                    style={{ backgroundColor: 'rgba(var(--accent-rgb, 59,130,246), 0.08)' }}>
                    <div className="flex flex-col items-center gap-2 px-6 py-4 rounded-xl"
                        style={{ backgroundColor: 'var(--bg-card)', border: '2px dashed var(--accent)', boxShadow: 'var(--shadow-lg)' }}>
                        <GripVertical size={24} style={{ color: 'var(--accent)' }} />
                        <span className="text-sm font-semibold" style={{ color: 'var(--accent)' }}>
                            Drop here to link content
                        </span>
                    </div>
                </div>
            )}

            {/* ─── Search Bar ─────────────────────────────── */}
            <div className="flex items-center gap-3">
                <div className="relative flex-1">
                    <Search size={splitMode ? 16 : 20} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none"
                        style={{ color: searchText ? 'var(--accent)' : 'var(--text-tertiary)' }} />
                    <input ref={searchInputRef} type="text" className="input w-full"
                        style={{
                            paddingLeft: '48px', paddingRight: searchText ? '40px' : '16px',
                            paddingTop: splitMode ? '10px' : '14px', paddingBottom: splitMode ? '10px' : '14px',
                            fontSize: splitMode ? '13px' : '15px',
                            borderRadius: 'var(--radius-lg)', backgroundColor: 'var(--bg-card)',
                            borderColor: searchText ? 'var(--accent)' : 'var(--border-default)',
                            boxShadow: searchText ? '0 0 0 3px var(--accent-soft)' : 'var(--shadow-sm)',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                        }}
                        placeholder="Search by name or description..."
                        value={searchText} onChange={e => setSearchText(e.target.value)} onKeyDown={handleKeyDown} />
                    {searchText && (
                        <button className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md"
                            style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer' }}
                            onClick={() => { setSearchText(''); searchInputRef.current?.focus(); }}>
                            <X size={16} />
                        </button>
                    )}
                </div>
                <button className="btn-primary flex items-center gap-2"
                    style={{ paddingTop: splitMode ? '8px' : '12px', paddingBottom: splitMode ? '8px' : '12px', paddingLeft: splitMode ? '16px' : '24px', paddingRight: splitMode ? '16px' : '24px', fontSize: splitMode ? '13px' : '15px' }}
                    onClick={runSearch} disabled={loading}>
                    {loading ? <Loader2 size={splitMode ? 14 : 18} className="animate-spin" /> : <Search size={splitMode ? 14 : 18} />}
                    Search
                </button>
            </div>

            {/* ─── Filter Panel ────────────────────────────── */}
            <div className="card p-4">
                {/* Saved Searches bar */}
                {!compact && (
                    <div className="flex items-center gap-3 mb-3 pb-3 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                        <div className="relative" ref={savedSearchRef}>
                            <button className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                                style={{ backgroundColor: savedSearchOpen ? 'var(--accent-soft)' : 'var(--surface-1)', color: savedSearchOpen ? 'var(--accent)' : 'var(--text-secondary)', border: '1px solid ' + (savedSearchOpen ? 'var(--accent)' : 'var(--border-default)'), cursor: 'pointer' }}
                                onClick={() => setSavedSearchOpen(!savedSearchOpen)}>
                                <Bookmark size={14} />
                                Saved Searches
                                {savedSearches.length > 0 && (
                                    <span className="flex items-center justify-center rounded-full text-xs font-semibold" style={{ width: 20, height: 20, backgroundColor: 'var(--accent)', color: 'white', flexShrink: 0 }}>{savedSearches.length}</span>
                                )}
                                <ChevronDown size={14} style={{ color: 'var(--text-tertiary)' }} />
                            </button>
                            {savedSearchOpen && (
                                <div className="absolute top-full left-0 mt-1 card p-2 z-50 max-h-80 overflow-auto" style={{ boxShadow: 'var(--shadow-lg)', minWidth: '320px' }}>
                                    {userSavedSearches.length > 0 && (
                                        <>
                                            <p className="text-xs font-semibold uppercase px-3 pt-2 pb-1" style={{ color: 'var(--text-tertiary)', letterSpacing: '0.05em' }}>My Searches</p>
                                            {userSavedSearches.map(s => (
                                                <div key={s.id} className="w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-start gap-2 group" style={{ cursor: 'pointer' }}
                                                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--accent-soft)'; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                                                    <Bookmark size={14} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
                                                    <div className="min-w-0 flex-1" onClick={() => applySavedSearch(s.settings)}>
                                                        <p className="font-medium truncate" style={{ color: 'var(--text-primary)' }}>{s.label}</p>
                                                        <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{[...(s.settings.clients || []), ...(s.settings.categories || []), ...(s.settings.applicationNames || []), ...(s.settings.tags || [])].filter(Boolean).join(' · ') || 'No filters'}</p>
                                                    </div>
                                                    <button className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }} title="Delete saved search"
                                                        onClick={e => { e.stopPropagation(); handleDeleteUserSearch(s.id); }}><Trash2 size={13} /></button>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                    {librarySavedSearches.length > 0 && (
                                        <>
                                            {userSavedSearches.length > 0 && <div className="border-t my-1" style={{ borderColor: 'var(--border-subtle)' }} />}
                                            <p className="text-xs font-semibold uppercase px-3 pt-2 pb-1" style={{ color: 'var(--text-tertiary)', letterSpacing: '0.05em' }}>Shared Searches</p>
                                            {librarySavedSearches.map(s => (
                                                <button key={s.id} className="w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-start gap-2"
                                                    style={{ color: 'var(--text-primary)', border: 'none', cursor: 'pointer', background: 'none' }}
                                                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--accent-soft)'; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                    onClick={() => applySavedSearch(s.settings)}>
                                                    <Bookmark size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0, marginTop: 2 }} />
                                                    <div className="min-w-0"><p className="font-medium truncate">{s.label}</p><p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{[...(s.settings.clients || []), ...(s.settings.categories || []), ...(s.settings.applicationNames || [])].filter(Boolean).join(' · ') || 'No filters'}</p></div>
                                                </button>
                                            ))}
                                        </>
                                    )}
                                    {savedSearches.length === 0 && <p className="text-xs text-center py-3" style={{ color: 'var(--text-tertiary)' }}>No saved searches yet</p>}
                                </div>
                            )}
                        </div>
                        {hasAnyCriteria && (
                            <button className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                                style={{ backgroundColor: 'var(--surface-1)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)', cursor: 'pointer' }}
                                onClick={() => { setSaveDialogOpen(true); setSaveSearchName(''); setTimeout(() => saveInputRef.current?.focus(), 50); }}>
                                <Save size={14} /> Save Search
                            </button>
                        )}
                        <span className="text-xs flex-1" style={{ color: 'var(--text-tertiary)' }}>
                            {savedSearches.length > 0 ? 'Select a saved search to pre-fill filters' : 'Save a search to quickly reuse filters'}
                        </span>
                    </div>
                )}

                {/* Save Search Dialog */}
                {saveDialogOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }} onClick={() => setSaveDialogOpen(false)}>
                        <div className="card p-5" style={{ width: 400, maxWidth: '90vw', boxShadow: 'var(--shadow-lg)' }} onClick={e => e.stopPropagation()}>
                            <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Save Search</h3>
                            <p className="text-xs mb-4" style={{ color: 'var(--text-tertiary)' }}>Give this search a name so you can find it later.</p>
                            <input ref={saveInputRef} type="text" className="w-full mb-3"
                                style={{ backgroundColor: 'var(--surface-0)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: '14px', outline: 'none' }}
                                placeholder="e.g. Electrical Services Legends" value={saveSearchName} onChange={e => setSaveSearchName(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && saveSearchName.trim()) handleSaveSearch(); }} />
                            <div className="text-xs mb-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-1)', color: 'var(--text-tertiary)' }}>
                                <p className="font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Criteria:</p>
                                {selectedClient && <p>• Client: {(selectedClient as any).name}</p>}
                                {categoryFilter.length > 0 && <p>• Categories: {categoryFilter.join(', ')}</p>}
                                {subCategoryFilter.length > 0 && <p>• Sub-Categories: {subCategoryFilter.join(', ')}</p>}
                                {appNameFilter.length > 0 && <p>• Applications: {appNameFilter.join(', ')}</p>}
                                {tagFilter.length > 0 && <p>• Tags: {tagFilter.join(', ')}</p>}
                                {searchText && <p>• Search: "{searchText}"</p>}
                            </div>
                            <div className="flex justify-end gap-2">
                                <button className="px-4 py-2 rounded-lg text-sm font-medium" style={{ background: 'none', border: '1px solid var(--border-default)', color: 'var(--text-secondary)', cursor: 'pointer' }} onClick={() => setSaveDialogOpen(false)}>Cancel</button>
                                <button className="btn-primary px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2" disabled={!saveSearchName.trim() || saving} onClick={handleSaveSearch}>
                                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className={`grid gap-3 ${splitMode ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5'}`}>
                    {/* Client Filter */}
                    <div className="relative" ref={clientRef}>
                        <label className="section-title block mb-1.5" style={{ fontSize: '11px' }}>Client</label>
                        <button className="input w-full text-left flex items-center gap-2" style={{ cursor: 'pointer' }}
                            onClick={() => { setClientDropdownOpen(!clientDropdownOpen); setClientSearchText(''); }}>
                            <Building2 size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                            <span className="flex-1 truncate" style={{ color: selectedClient ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                                {selectedClient ? (selectedClient as any).name : 'All Clients'}
                            </span>
                            <ChevronDown size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                        </button>
                        {clientDropdownOpen && (
                            <div className="absolute top-full left-0 right-0 mt-1 card p-2 z-50 max-h-56 overflow-auto" style={{ boxShadow: 'var(--shadow-lg)' }}>
                                <input type="text" className="input w-full mb-2"
                                    style={{ fontSize: '13px', backgroundColor: 'var(--surface-0)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '8px 12px', outline: 'none' }}
                                    placeholder="Search clients..." value={clientSearchText} onChange={e => setClientSearchText(e.target.value)} autoFocus />
                                <button className="w-full text-left px-3 py-2 rounded-md text-sm transition-colors"
                                    style={{ backgroundColor: !selectedClientId ? 'var(--accent-soft)' : 'transparent', color: !selectedClientId ? 'var(--accent)' : 'var(--text-secondary)', border: 'none', cursor: 'pointer' }}
                                    onClick={() => { setSelectedClientId(null); setClientDropdownOpen(false); }}>All Clients</button>
                                {filteredClients.map((c: any) => (
                                    <button key={c.id} className="w-full text-left px-3 py-2 rounded-md text-sm transition-colors"
                                        style={{ backgroundColor: selectedClientId === c.id ? 'var(--accent-soft)' : 'transparent', color: selectedClientId === c.id ? 'var(--accent)' : 'var(--text-primary)', border: 'none', cursor: 'pointer' }}
                                        onMouseEnter={e => { if (selectedClientId !== c.id) e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                                        onMouseLeave={e => { if (selectedClientId !== c.id) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                        onClick={() => { setSelectedClientId(c.id); setClientDropdownOpen(false); }}>{c.name}</button>
                                ))}
                                {filteredClients.length === 0 && <p className="text-xs text-center py-2" style={{ color: 'var(--text-tertiary)' }}>No clients found</p>}
                            </div>
                        )}
                    </div>

                    <FilterSelect label="Category" values={categoryFilter} options={categories} placeholder="All Categories"
                        onChange={vals => { setCategoryFilter(vals); setVisibleCount(PAGE_SIZE); }}
                        icon={<FolderOpen size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />} />

                    {!splitMode && (
                        <FilterSelect label="Sub-Category" values={subCategoryFilter} options={subCategories} placeholder="All Sub-Categories"
                            onChange={vals => { setSubCategoryFilter(vals); setVisibleCount(PAGE_SIZE); }}
                            icon={<FolderOpen size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />} />
                    )}

                    <FilterSelect label="Application" values={appNameFilter} options={appNames} placeholder="All Applications"
                        onChange={vals => { setAppNameFilter(vals); setVisibleCount(PAGE_SIZE); }}
                        icon={<Layers size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />} />

                    {!splitMode && (
                        <FilterSelect label="Tags" values={tagFilter} options={tagOptions} placeholder="All Tags"
                            onChange={vals => { setTagFilter(vals); setVisibleCount(PAGE_SIZE); }}
                            icon={<Tag size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />} />
                    )}
                </div>

                {/* Active filter badges */}
                {activeFilterCount > 0 && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t flex-wrap" style={{ borderColor: 'var(--border-subtle)' }}>
                        <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>Active:</span>
                        {selectedClient && (
                            <span className="badge-accent flex items-center gap-1">
                                <Building2 size={11} /> {(selectedClient as any).name}
                                <button onClick={() => setSelectedClientId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', marginLeft: 2 }}><X size={11} /></button>
                            </span>
                        )}
                        {categoryFilter.map(cat => <span key={`cat-${cat}`} className="badge-accent flex items-center gap-1"><FolderOpen size={11} /> {cat}<button onClick={() => setCategoryFilter(categoryFilter.filter(c => c !== cat))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', marginLeft: 2 }}><X size={11} /></button></span>)}
                        {subCategoryFilter.map(sub => <span key={`sub-${sub}`} className="badge-accent flex items-center gap-1"><FolderOpen size={11} /> {sub}<button onClick={() => setSubCategoryFilter(subCategoryFilter.filter(s => s !== sub))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', marginLeft: 2 }}><X size={11} /></button></span>)}
                        {appNameFilter.map(app => <span key={`app-${app}`} className="badge-accent flex items-center gap-1"><Layers size={11} /> {app}<button onClick={() => setAppNameFilter(appNameFilter.filter(a => a !== app))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', marginLeft: 2 }}><X size={11} /></button></span>)}
                        {tagFilter.map(tag => {
                            const selectedTag = allTags.find(t => t.name === tag);
                            return (
                                <TagBadge
                                    key={`tag-${tag}`}
                                    tag={{ name: tag, type: selectedTag?.type }}
                                    showType={false}
                                    className="flex items-center gap-1"
                                >
                                    <Tag size={11} /> {tag}
                                    <button onClick={() => setTagFilter(tagFilter.filter(t => t !== tag))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', marginLeft: 2 }}><X size={11} /></button>
                                </TagBadge>
                            );
                        })}
                        <button className="text-xs font-medium ml-1" style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }} onClick={clearAllFilters}>Clear all</button>
                    </div>
                )}
            </div>

            {/* ─── Pre-search state ───────────────────────── */}
            {!hasSearched && !loading && (
                <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4 animate-fade-in">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'var(--accent-soft)' }}>
                        <Sparkles size={32} style={{ color: 'var(--accent)' }} />
                    </div>
                    <div className="text-center">
                        <p className="text-base font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Ready to search</p>
                        <p className="text-sm" style={{ color: 'var(--text-tertiary)', maxWidth: 420 }}>
                            {splitMode
                                ? <>Search for content, then <strong>drag cards</strong> to the other pane to link them.</>
                                : <>Type a search term and/or select filters above, then click <strong>Search</strong> or press <strong>Enter</strong> to find content.</>
                            }
                        </p>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                        {hasAnyCriteria && <button className="btn-primary flex items-center gap-2" onClick={runSearch}><Search size={16} /> Search Now</button>}
                        {!compact && favouriteIds.size > 0 && (
                            <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                style={{ backgroundColor: 'rgba(234,179,8,0.1)', color: '#b45309', border: '1px solid rgba(234,179,8,0.3)', cursor: 'pointer' }}
                                onClick={showFavourites}>
                                <Star size={15} fill="#eab308" style={{ color: '#eab308' }} /> Show Favourites ({favouriteIds.size})
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* ─── Results ────────────────────────────────── */}
            {(hasSearched || loading) && (
                <>
                    {/* Results toolbar */}
                    <div className="flex items-center justify-between">
                        <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                            {loading ? (
                                <span className="flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Searching...</span>
                            ) : (
                                <>Showing <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{Math.min(visibleCount, filteredResults.length)}</span> of <span style={{ fontWeight: 600 }}>{filteredResults.length}</span> result{filteredResults.length !== 1 ? 's' : ''}</>
                            )}
                        </p>
                        <div className="flex items-center gap-2">
                            {!compact && favouriteIds.size > 0 && (
                                <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                                    style={{ backgroundColor: showingFavourites ? 'rgba(234,179,8,0.15)' : 'var(--surface-100)', color: showingFavourites ? '#b45309' : 'var(--text-secondary)', border: showingFavourites ? '1px solid rgba(234,179,8,0.4)' : '1px solid transparent', cursor: 'pointer', outline: 'none' }}
                                    onClick={() => { if (showingFavourites) { setShowingFavourites(false); runSearch(); } else { showFavourites(); } }}
                                    title={showingFavourites ? 'Back to search results' : 'Show favourites'}>
                                    <Star size={14} fill={showingFavourites ? '#eab308' : 'none'} style={{ color: showingFavourites ? '#eab308' : 'var(--text-tertiary)' }} />
                                    {showingFavourites ? 'All Results' : `Favourites (${favouriteIds.size})`}
                                </button>
                            )}
                            <div className="relative" onClick={e => e.stopPropagation()}>
                                <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                                    style={{ backgroundColor: 'var(--surface-100)', color: 'var(--text-secondary)', border: 'none', cursor: 'pointer', outline: 'none' }}
                                    onClick={() => setSortDropdownOpen(!sortDropdownOpen)}>
                                    <ArrowUpDown size={14} /> {sortLabels[sortBy]} <ChevronDown size={12} />
                                </button>
                                {sortDropdownOpen && (
                                    <div className="absolute top-full right-0 mt-1 card p-1.5 z-50 min-w-40" style={{ boxShadow: 'var(--shadow-lg)' }}>
                                        {(Object.entries(sortLabels) as [SortOption, string][]).map(([key, label]) => (
                                            <button key={key} className="w-full text-left px-3 py-2 rounded-md text-sm transition-colors"
                                                style={{ backgroundColor: sortBy === key ? 'var(--accent-soft)' : 'transparent', color: sortBy === key ? 'var(--accent)' : 'var(--text-primary)', border: 'none', cursor: 'pointer' }}
                                                onMouseEnter={e => { if (sortBy !== key) e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                                                onMouseLeave={e => { if (sortBy !== key) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                onClick={() => { setSortBy(key); setSortDropdownOpen(false); }}>{label}</button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--surface-100)' }}>
                                <button className="p-2 rounded-md transition-all"
                                    style={{ backgroundColor: viewMode === 'grid' ? 'var(--bg-card)' : 'transparent', boxShadow: viewMode === 'grid' ? 'var(--shadow-sm)' : 'none', color: viewMode === 'grid' ? 'var(--text-primary)' : 'var(--text-tertiary)', border: 'none', cursor: 'pointer', outline: 'none' }}
                                    onClick={() => setViewMode('grid')}><LayoutGrid size={16} /></button>
                                <button className="p-2 rounded-md transition-all"
                                    style={{ backgroundColor: viewMode === 'list' ? 'var(--bg-card)' : 'transparent', boxShadow: viewMode === 'list' ? 'var(--shadow-sm)' : 'none', color: viewMode === 'list' ? 'var(--text-primary)' : 'var(--text-tertiary)', border: 'none', cursor: 'pointer', outline: 'none' }}
                                    onClick={() => setViewMode('list')}><List size={16} /></button>
                            </div>
                        </div>
                    </div>

                    {/* Results area */}
                    <div className="flex-1 min-h-0 overflow-auto">
                        {loading ? (
                            <SpinnerNew size="large" label="Searching content..." />
                        ) : filteredResults.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-state-icon"><Search size={24} /></div>
                                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>No content matches your search</p>
                                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Try broadening your search terms or removing some filters</p>
                                <button className="btn-secondary mt-3 flex items-center gap-1.5" onClick={clearAllFilters}><X size={14} /> Clear All</button>
                            </div>
                        ) : viewMode === 'grid' ? (
                            <>
                                <div className={`grid gap-4 p-1 animate-fade-in ${splitMode ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}>
                                    {visibleResults.map((cv: any) => (
                                        <div key={cv.id} className="card-interactive p-0 overflow-hidden group"
                                            style={{
                                                cursor: splitMode ? 'grab' : 'pointer',
                                                outline: cardDragOver === cv.contentId ? '2px solid var(--accent)' : undefined,
                                                boxShadow: cardDragOver === cv.contentId ? '0 0 0 4px var(--accent-soft)' : undefined,
                                                transform: cardDragOver === cv.contentId ? 'scale(1.02)' : undefined,
                                                transition: 'outline 0.15s, box-shadow 0.15s, transform 0.15s',
                                            }}
                                            draggable={splitMode}
                                            onDragStart={splitMode ? (e) => handleDragStart(e, cv.contentId, getContentDisplayName(cv)) : undefined}
                                            onDragOver={splitMode ? (e) => handleCardDragOver(e, cv.contentId) : undefined}
                                            onDragLeave={splitMode ? handleCardDragLeave : undefined}
                                            onDrop={splitMode ? (e) => handleCardDropOnCard(e, cv.contentId, getContentDisplayName(cv)) : undefined}
                                            onClick={() => onOpenDetail?.(cv.contentId, cv.clientId)}>
                                            <div className="relative h-36 overflow-hidden flex items-center justify-center" style={{ backgroundColor: 'var(--surface-100)' }}>
                                                <ThumbnailDisplay cv={cv} onZoom={url => onZoom?.(url)} />
                                                {/* Drag handle indicator in split mode */}
                                                {splitMode && (
                                                    <div className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-70 transition-opacity"
                                                        style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: 'white' }}>
                                                        <GripVertical size={14} />
                                                    </div>
                                                )}
                                                {!splitMode && (
                                                    <button className="absolute top-2 left-2 p-1.5 rounded-full transition-all"
                                                        style={{ backgroundColor: favouriteIds.has(cv.contentId) ? 'rgba(234,179,8,0.15)' : 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)', border: 'none', cursor: togglingFavourite === cv.contentId ? 'wait' : 'pointer', opacity: togglingFavourite === cv.contentId ? 0.5 : 1 }}
                                                        title={favouriteIds.has(cv.contentId) ? 'Remove from favourites' : 'Add to favourites'}
                                                        onClick={e => { e.stopPropagation(); toggleFavourite(cv.contentId, getContentDisplayName(cv)); }}>
                                                        <Star size={14} fill={favouriteIds.has(cv.contentId) ? '#eab308' : 'none'} style={{ color: favouriteIds.has(cv.contentId) ? '#eab308' : 'white' }} />
                                                    </button>
                                                )}
                                                {cv.content?.usageCount > 0 && (
                                                    <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium"
                                                        style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', backdropFilter: 'blur(4px)', display: splitMode ? 'none' : undefined }}>
                                                        <Eye size={11} /> {cv.content.usageCount}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-4">
                                                <h3 className="text-sm font-semibold mb-1 line-clamp-1" style={{ color: 'var(--text-primary)' }} title={getContentDisplayName(cv)}>{getContentDisplayName(cv)}</h3>
                                                {cv.description && !splitMode && <p className="text-xs mb-3 line-clamp-2" style={{ color: 'var(--text-tertiary)' }}>{cv.description}</p>}
                                                <div className="flex flex-col gap-1.5 mb-3">
                                                    {cv.category && (
                                                        <div className="flex items-center gap-1.5">
                                                            <FolderOpen size={12} style={{ color: 'var(--accent)' }} />
                                                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{cv.category}{cv.subCategory && ` › ${cv.subCategory}`}</span>
                                                        </div>
                                                    )}
                                                    {cv.applicationName && (
                                                        <div className="flex items-center gap-1.5">
                                                            <Layers size={12} style={{ color: 'var(--info)' }} />
                                                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{cv.applicationName}</span>
                                                        </div>
                                                    )}
                                                    {cv.library?.name && (
                                                        <div className="flex items-center gap-1.5">
                                                            <Building2 size={12} style={{ color: 'var(--warning)' }} />
                                                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{cv.library.client?.name ? `${cv.library.client.name} › ` : ''}{cv.library.name}</span>
                                                        </div>
                                                    )}
                                                    {cv.createdTime && !splitMode && (
                                                        <div className="flex items-center gap-1.5">
                                                            <Clock size={12} style={{ color: 'var(--text-tertiary)' }} />
                                                            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{formatDate(cv.createdTime)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                {!splitMode && cv.content?.tags && cv.content.tags.filter(Boolean).length > 0 && (
                                                    <div className="flex flex-wrap gap-1 pt-3 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                                                        {cv.content.tags.filter(Boolean).slice(0, 3).map((t: any) => <TagBadge key={t.id} tag={t} showType={false} className="text-xxs" />)}
                                                        {cv.content.tags.filter(Boolean).length > 3 && <span className="badge-default text-xxs">+{cv.content.tags.filter(Boolean).length - 3}</span>}
                                                    </div>
                                                )}
                                                {!splitMode && cv.content?.classifications && cv.content.classifications.filter(Boolean).length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-2">
                                                        {cv.content.classifications.filter(Boolean).slice(0, 2).map((c: any) => <span key={c.id} className="badge-default text-xxs">{c.type?.name ? `${c.type.name}: ` : ''}{c.name}</span>)}
                                                        {cv.content.classifications.filter(Boolean).length > 2 && <span className="badge-default text-xxs">+{cv.content.classifications.filter(Boolean).length - 2}</span>}
                                                    </div>
                                                )}
                                                {!splitMode && cv.applicationName?.toLowerCase() === 'pdf' && cv.attachment?.downloadUrl && (
                                                    <button className="flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg text-xs font-medium w-full justify-center transition-colors"
                                                        style={{ backgroundColor: 'var(--accent-soft)', color: 'var(--accent)', border: '1px solid var(--accent)', cursor: 'pointer' }}
                                                        onClick={e => { e.stopPropagation(); openPdfInNewTab(cv.attachment.downloadUrl, getContentDisplayName(cv) || 'Document'); }}>
                                                        <FileText size={13} /> View PDF <ExternalLink size={11} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {hasMore && (
                                    <div className="flex justify-center mt-6">
                                        <button className="btn-secondary flex items-center gap-2" onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}>
                                            Show More <ChevronRight size={14} />
                                            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>({filteredResults.length - visibleCount} remaining)</span>
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            /* List view */
                            <>
                                <div className="card overflow-auto animate-fade-in" style={{ maxHeight: '100%' }}>
                                    <table className="w-full border-collapse">
                                        <thead className="sticky top-0 z-10" style={{ backgroundColor: 'var(--bg-card)' }}>
                                            <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                                                {splitMode && <th className="px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)', width: 30 }}></th>}
                                                {!splitMode && <th className="px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)', width: 40 }}><Star size={13} style={{ color: 'var(--text-tertiary)', margin: '0 auto' }} /></th>}
                                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>Content</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>Category</th>
                                                {!splitMode && <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider hidden lg:table-cell" style={{ color: 'var(--text-tertiary)' }}>Application</th>}
                                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider hidden md:table-cell" style={{ color: 'var(--text-tertiary)' }}>Client / Library</th>
                                                {!splitMode && <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider hidden xl:table-cell" style={{ color: 'var(--text-tertiary)' }}>Tags</th>}
                                                {!splitMode && <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>Usage</th>}
                                                {!splitMode && <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider hidden sm:table-cell" style={{ color: 'var(--text-tertiary)' }}>Created</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleResults.map((cv: any) => (
                                                <tr key={cv.id} className="transition-colors duration-100"
                                                    style={{
                                                        borderBottom: '1px solid var(--border-subtle)',
                                                        cursor: splitMode ? 'grab' : 'pointer',
                                                        outline: cardDragOver === cv.contentId ? '2px solid var(--accent)' : undefined,
                                                        backgroundColor: cardDragOver === cv.contentId ? 'var(--accent-soft)' : undefined,
                                                    }}
                                                    draggable={splitMode}
                                                    onDragStart={splitMode ? (e) => handleDragStart(e, cv.contentId, getContentDisplayName(cv)) : undefined}
                                                    onDragOver={splitMode ? (e) => handleCardDragOver(e, cv.contentId) : undefined}
                                                    onDragLeave={splitMode ? handleCardDragLeave : undefined}
                                                    onDrop={splitMode ? (e) => handleCardDropOnCard(e, cv.contentId, getContentDisplayName(cv)) : undefined}
                                                    onMouseOver={e => { if (!cardDragOver) e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                                                    onMouseOut={e => { if (!cardDragOver) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                    onClick={() => onOpenDetail?.(cv.contentId, cv.clientId)}>
                                                    {splitMode && (
                                                        <td className="px-2 py-3 text-center" style={{ width: 30 }}>
                                                            <GripVertical size={14} style={{ color: 'var(--text-tertiary)', opacity: 0.5 }} />
                                                        </td>
                                                    )}
                                                    {!splitMode && (
                                                        <td className="px-2 py-3 text-center" style={{ width: 40 }}>
                                                            <button className="p-1 rounded-full transition-all" style={{ background: 'none', border: 'none', cursor: togglingFavourite === cv.contentId ? 'wait' : 'pointer', opacity: togglingFavourite === cv.contentId ? 0.5 : 1 }}
                                                                onClick={e => { e.stopPropagation(); toggleFavourite(cv.contentId, getContentDisplayName(cv)); }}>
                                                                <Star size={15} fill={favouriteIds.has(cv.contentId) ? '#eab308' : 'none'} style={{ color: favouriteIds.has(cv.contentId) ? '#eab308' : 'var(--text-tertiary)' }} />
                                                            </button>
                                                        </td>
                                                    )}
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: 'var(--surface-100)' }}>
                                                                {(() => { const thumb = getPrimaryThumbnail(cv) || getTypeThumbnails(cv)[0]?.url; return thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" /> : <Package size={18} style={{ color: 'var(--text-tertiary)', opacity: 0.5 }} />; })()}
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{getContentDisplayName(cv)}</p>
                                                                {cv.description && <p className="text-xs line-clamp-1" style={{ color: 'var(--text-tertiary)' }}>{cv.description}</p>}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{cv.category || '—'}</span>
                                                        {cv.subCategory && <span className="text-xs block" style={{ color: 'var(--text-tertiary)' }}>{cv.subCategory}</span>}
                                                    </td>
                                                    {!splitMode && <td className="px-4 py-3 hidden lg:table-cell"><span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{cv.applicationName || '—'}</span></td>}
                                                    <td className="px-4 py-3 hidden md:table-cell"><span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{cv.library?.client?.name ? `${cv.library.client.name} › ` : ''}{cv.library?.name || '—'}</span></td>
                                                    {!splitMode && <td className="px-4 py-3 hidden xl:table-cell"><div className="flex flex-wrap gap-1">{cv.content?.tags?.filter(Boolean).slice(0, 3).map((t: any) => <TagBadge key={t.id} tag={t} showType={false} className="text-xxs" />)}{(cv.content?.tags?.filter(Boolean).length || 0) > 3 && <span className="badge-default text-xxs">+{cv.content.tags.filter(Boolean).length - 3}</span>}</div></td>}
                                                    {!splitMode && <td className="px-4 py-3 text-right"><span className="text-sm font-medium" style={{ color: (cv.content?.usageCount || 0) > 0 ? 'var(--accent)' : 'var(--text-tertiary)' }}>{cv.content?.usageCount || 0}</span></td>}
                                                    {!splitMode && <td className="px-4 py-3 text-right hidden sm:table-cell"><span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{formatDate(cv.createdTime)}</span></td>}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {hasMore && (
                                    <div className="flex justify-center mt-4">
                                        <button className="btn-secondary flex items-center gap-2" onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}>
                                            Show More <ChevronRight size={14} />
                                            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>({filteredResults.length - visibleCount} remaining)</span>
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ContentSearchPane;
